import React, { useContext, useEffect, useState } from "react";
import { generateUUID } from "go-report-combo/User/services/services";
import { CreateTemplateReportComboComponent } from "go-report-combo/User/template/Template/CreateTemplateReportComboComponent";
import { CatalogReportCombo, TemplateReportCombo } from "go-report-combo/User/template/Template/types";
import queryString from "query-string";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { SegmentContext } from "go-segment/context";
import { SegmentApi } from "go-segment/services/types";
import ComboTemplateUtils from "../../services/comboTemplateUtils";
import { ReportComboContext } from "../../services/context";
import { ConfigReportComboApi } from "../../services/types";

const comboName = "REPORT_COMBO";
const resourceType = "REPORT_COMBO";

const parseFetchedCatalogs = (fetchedCatalogs: ConfigReportComboApi[]) =>
	fetchedCatalogs.map((fetchedCatalog) => ({
		id: fetchedCatalog.id,
		name: fetchedCatalog.name,
		description: fetchedCatalog.name,
		category: "BASIC",
		data: fetchedCatalog,
	}));

export const UserReportsComboCreatePage = () => {
	const [template, setTemplate] = useState<TemplateReportCombo | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [segment, setSegment] = useState<SegmentApi | undefined>(undefined);
	const [errors, setErrors] = useState<ApiError[]>([]);
	const [catalog, setCatalog] = useState<CatalogReportCombo[] | undefined>(undefined);
	const segmentContext = useContext(SegmentContext);
	const reportComboContext = useContext(ReportComboContext);
	const segmentCRUDRedirectPath = reportComboContext.getSegmentCRUDRedirectPath();
	const location = useLocation();
	const history = useHistory();
	const queryParams = queryString.parse(location.search);
	const segmentId = queryParams.segmentId;
	const { addFlash } = useFlash();

	useEffect(() => {
		getComboReportData();
	}, []);

	const getComboReportData = async () => {
		try {
			const catalogsPromise = reportComboContext.api().getTemplates();
			let catalogs: ConfigReportComboApi[] | null = null;

			if (segmentId) {
				const filter = `resource_type|e=${resourceType}${FILTER_SEPARATOR}list_name|e=${comboName}${FILTER_SEPARATOR}id|e=${segmentId}`;
				const segmentsPromise = segmentContext.api().getSegments({
					f: btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${filter}`))),
				});

				const [segments, fetchedCatalogs] = await Promise.all([segmentsPromise, catalogsPromise]);
				const segment = segments.data.data[0];
				catalogs = fetchedCatalogs;

				if (segment) {
					setTemplate(ComboTemplateUtils.segmentToTemplate(segment));
				} else {
					setTemplate(undefined);
				}

				setSegment(segment);
			} else {
				catalogs = await catalogsPromise;

				setTemplate({
					id: generateUUID(),
					name: "Nowy raport",
					templates: [],
				} as TemplateReportCombo);
			}
			const parsedCatalogs: CatalogReportCombo[] = parseFetchedCatalogs(catalogs);

			setCatalog(parsedCatalogs);
		} catch (err) {
			handleError.alert(err, addFlash);
		} finally {
			setLoading(false);
		}
	};

	const saveTemplate = async (template: TemplateReportCombo) => {
		if (!template) return;

		const value = JSON.stringify(template);
		const newSegment = {
			name: template.name,
			slug: template.id,
			list_name: comboName,
			resource_type: resourceType,
			value,
		} as SegmentApi;

		let segmentResponse: Record<string, any> | undefined = undefined;
		try {
			if (segment) {
				newSegment.id = segment.id;
				const updatedSegment = await segmentContext.api().updateSegment(newSegment);
				segmentResponse = updatedSegment.data.data;
			} else {
				const createdSegment = await segmentContext.api().saveSegment(newSegment);
				segmentResponse = createdSegment.data.data;
			}

			if (segmentResponse?.id) history.push(`${segmentCRUDRedirectPath}/${segmentResponse.id}`);
		} catch (err) {
			const errors = handleException(err);
			setErrors(errors);
			handleError.alert(err, addFlash);
		}
	};

	const onClose = async () => {
		if (segment) {
			history.push(`${segmentCRUDRedirectPath}/${segment.id}`);
		} else {
			history.push(segmentCRUDRedirectPath);
		}
	};

	const onRemove = async () => {
		try {
			if (segment) {
				await segmentContext.api().removeSegment(segment.id);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		history.push(segmentCRUDRedirectPath);
	};

	if (loading || !catalog || !template) {
		return <LoadingContainer />;
	}

	if (segmentId && !segment) {
		return <div>Segment nie istnieje</div>;
	}

	return (
		<CreateTemplateReportComboComponent
			catalog={catalog}
			template={template}
			errors={errors}
			onClose={onClose}
			onRemove={onRemove}
			onSave={saveTemplate}
		/>
	);
};
